/**
 * DriveZone — Subscribe Pending Page
 * Route: /subscribe/pending  (protected)
 *
 * Shown immediately after STK Push is sent.
 * User is on their phone entering M-Pesa PIN.
 * Polls /api/payments/status/ every 3 seconds.
 * Shows countdown from 3:00. "I already paid" triggers manual Daraja query.
 */

import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import api from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'

const POLL_INTERVAL_MS = 3000
const MAX_POLLS        = 20          // 20 × 3s = 60 s of polling
const COUNTDOWN_SECS   = 180         // 3 minutes (Safaricom STK Push validity)

interface PendingState {
  subscription_id:     number
  checkout_request_id: string | null
  phone_masked:        string
  amount:              number
  currency:            string
  country_name:        string
}

interface StatusResponse {
  subscription_status: string
  is_subscribed:       boolean
  period_end:          string | null
  mpesa_receipt:       string | null
}

export default function SubscribePending() {
  const navigate        = useNavigate()
  const location        = useLocation()
  const { updateUser }  = useAuthStore()
  const state           = (location.state as PendingState) ?? {}

  const phoneMasked = state.phone_masked ?? ''
  const amount      = state.amount       ?? 999

  const [pollCount,  setPollCount]  = useState(0)
  const [timeLeft,   setTimeLeft]   = useState(COUNTDOWN_SECS)
  const [checkingManually, setCheckingManually] = useState(false)
  const [manualError,      setManualError]      = useState('')
  const [timedOut, setTimedOut] = useState(false)

  const pollRef      = useRef<ReturnType<typeof setInterval>>()
  const countdownRef = useRef<ReturnType<typeof setInterval>>()

  // ── Countdown timer ──────────────────────────────────────────────────────
  useEffect(() => {
    countdownRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(countdownRef.current)
          return 0
        }
        return t - 1
      })
    }, 1000)
    return () => clearInterval(countdownRef.current)
  }, [])

  // ── Status polling ───────────────────────────────────────────────────────
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get<StatusResponse>('/api/payments/status/')
        if (res.data.is_subscribed) {
          clearInterval(pollRef.current)
          clearInterval(countdownRef.current)
          updateUser({ subscription_status: 'active', is_subscribed: true, can_search: true })
          navigate('/subscribe/success', {
            state: { receipt: res.data.mpesa_receipt, period_end: res.data.period_end, amount },
            replace: true,
          })
        }
      } catch {
        // Network hiccup — keep polling
      }

      setPollCount((c) => {
        const next = c + 1
        if (next >= MAX_POLLS) {
          clearInterval(pollRef.current)
          setTimedOut(true)
        }
        return next
      })
    }, POLL_INTERVAL_MS)

    return () => clearInterval(pollRef.current)
  }, [navigate, updateUser, amount])

  // ── "I already paid" ──────────────────────────────────────────────────────
  async function handleManualCheck() {
    setManualError('')
    setCheckingManually(true)
    try {
      const res = await api.post<{ message: string; status: string }>(
        '/api/payments/check-status/'
      )
      if (res.data.status === 'activating') {
        // Will be caught by the next poll cycle
      } else if (res.data.status === 'failed') {
        setManualError('Payment was not completed. Please try again.')
      }
    } catch {
      setManualError('Could not check status. Please wait and try again.')
    } finally {
      setTimeout(() => setCheckingManually(false), 5000)
    }
  }

  function formatTime(secs: number) {
    const m = Math.floor(secs / 60).toString().padStart(2, '0')
    const s = (secs % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  // ── Timed out ─────────────────────────────────────────────────────────────
  if (timedOut || timeLeft === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm card p-8 text-center space-y-5">
          <AlertCircle className="w-14 h-14 text-amber-400 mx-auto" />
          <div>
            <h1 className="text-xl font-bold text-slate-900">Payment request expired</h1>
            <p className="text-sm text-slate-500 mt-2">
              The M-Pesa prompt has expired. If you completed the payment,
              check your account in a few minutes.
            </p>
          </div>
          <div className="space-y-2">
            <button onClick={() => navigate('/subscribe', { replace: true })} className="btn-primary w-full">
              Try again
            </button>
            <button onClick={() => navigate('/account')} className="btn-secondary w-full text-sm">
              Check account
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Waiting ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm card p-8 text-center space-y-6">

        {/* Phone icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center">
            <span className="text-3xl">📱</span>
          </div>
        </div>

        {/* Headline */}
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-slate-900">Check your phone</h1>
          {phoneMasked && (
            <p className="text-sm text-slate-500">
              We sent an M-Pesa request to <span className="font-mono font-medium text-slate-700">{phoneMasked}</span>
            </p>
          )}
          <p className="text-sm text-slate-500 mt-2">
            Enter your M-Pesa PIN to complete the payment of{' '}
            <span className="font-semibold text-slate-800">$1.55 USD</span>
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5">
          {Array.from({ length: MAX_POLLS }).map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-500
                ${i < pollCount ? 'bg-primary-500' : 'bg-slate-200'}`}
            />
          ))}
        </div>
        <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Waiting for confirmation…
        </p>

        {/* Manual check */}
        <div className="space-y-2">
          {manualError && (
            <p className="text-xs text-red-500">{manualError}</p>
          )}
          <button
            onClick={handleManualCheck}
            disabled={checkingManually}
            className="w-full flex items-center justify-center gap-2
                       text-sm font-medium text-primary-600 hover:text-primary-700
                       disabled:opacity-50 transition-colors py-2"
          >
            {checkingManually ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</>
            ) : (
              <><RefreshCw className="w-4 h-4" /> I already paid — check status</>
            )}
          </button>

          <button
            onClick={async () => {
              try { await api.post('/api/payments/cancel/') } catch { /* ignore */ }
              navigate('/subscribe', { replace: true })
            }}
            className="w-full text-sm text-slate-400 hover:text-slate-600 transition-colors py-1"
          >
            Cancel and go back
          </button>
        </div>

        {/* Countdown */}
        <p className="text-xs text-slate-400">
          Request expires in{' '}
          <span className={`font-mono font-semibold ${timeLeft < 30 ? 'text-red-500' : 'text-slate-600'}`}>
            {formatTime(timeLeft)}
          </span>
        </p>
      </div>
    </div>
  )
}
